import { type RefObject, useCallback } from "react";

const getMarkerInputs = (containerRef: RefObject<HTMLElement | null>) =>
  containerRef.current?.querySelectorAll<HTMLElement>("input.marker, textarea.marker");

/**
 * Returns a stable callback that focuses the next `input.marker` element
 * within the container when called (typically from onPressEnter).
 * Detects the currently focused input via document.activeElement.
 * Returns true if it focused the next input, false if already at the last one.
 */
export const useEnterToNext = (containerRef: RefObject<HTMLElement | null>) =>
  useCallback(() => {
    const inputs = getMarkerInputs(containerRef);
    if (!inputs) return false;
    const current = document.activeElement;
    for (let i = 0; i < inputs.length - 1; i++) {
      if (inputs[i] === current) {
        const next = inputs[i + 1];
        // Defer focus to avoid Enter keystroke leaking into the next element (e.g. textarea newline)
        requestAnimationFrame(() => next.focus());
        return true;
      }
    }
    return false;
  }, [containerRef]);

/**
 * Returns helpers to focus a marker input by index.
 * `focusAt(index)` focuses immediately, returns true if found.
 * `focusAtDeferred(index)` returns a callback that focuses after a short delay (for post-mutation focus).
 */
export const useFocusMarker = (containerRef: RefObject<HTMLElement | null>) => {
  const focusAt = useCallback(
    (index: number) => {
      const input = getMarkerInputs(containerRef)?.[index];
      if (input) {
        input.focus();
        return true;
      }
      return false;
    },
    [containerRef],
  );

  const focusAtDeferred = useCallback((index: number) => () => setTimeout(() => focusAt(index), 100), [focusAt]);

  return { focusAt, focusAtDeferred };
};
